import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useTenant } from '../hooks/useTenant';
import { profileAPI } from '../services/api';
import { PermissionsContext } from './PermissionsContext';

const NONE = new Set();

/**
 * The caller's effective Hub permissions (`resource:action`, e.g.
 * `building:create`) in the active tenant, from GET /profile/permissions.
 * Reloaded when the user or the active tenant changes. `has(...perms)` is true
 * when ANY of them is held, and false while loading or after a failed load, so
 * gated UI stays hidden until the server has answered. The API still enforces
 * every check; this only stops the UI offering actions it would refuse.
 * Depends on AuthProvider and TenantProvider.
 */
export const PermissionsProvider = ({ children }) => {
  const { user } = useAuth();
  const { activeTenant, loading: tenantLoading } = useTenant();
  const userId = user?.id;
  const tenantId = activeTenant?.id;
  const tenantRole = activeTenant?.role;
  const [permissions, setPermissions] = useState(NONE);
  const [loading, setLoading] = useState(true);
  const latestRequest = useRef(0);

  useEffect(() => {
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    const isLatest = () => requestId === latestRequest.current;

    if (!userId) {
      setPermissions(NONE);
      setLoading(false);
      return;
    }
    // The answer depends on the tenant (auditor memberships are read-only),
    // so wait until the active tenant is known rather than asking twice.
    if (tenantLoading) return;

    setLoading(true);
    profileAPI
      .getPermissions()
      .then((data) => {
        if (isLatest()) setPermissions(new Set(data?.permissions || []));
      })
      .catch(() => {
        if (isLatest()) setPermissions(NONE);
      })
      .finally(() => {
        if (isLatest()) setLoading(false);
      });
  }, [userId, tenantId, tenantRole, tenantLoading]);

  const has = useCallback((...wanted) => wanted.some((p) => permissions.has(p)), [permissions]);

  const value = useMemo(() => ({ loading, permissions, has }), [loading, permissions, has]);

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};
